import React, { useState, useCallback, useMemo } from 'react';


import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import { Radio } from 'ui/radio';
import { RadioGroup } from 'ui/radio-group';

import EditRangesModal from './edit-ranges-modal';

import {
  AWARD_TYPE_OPTIONS,
  CALCULATED_BY_OPTIONS,
  getRangesLabel,
  formatRangeLabel,
  sortRangesForDisplay,
  isNonAidRange,
  isPellRange,
} from '../definition-tab.constants';

import Icons from 'assets/icons';

import type { Range } from '../definition-tab.constants';

type AcademicRank = { id: string | number; rank: string | number };

type DefinitionTabContentProps = {
  awardType: string;
  calculatedBy: string;
  ranges: Range[];
  academicRanks?: AcademicRank[];
  onAwardTypeChange: (val: string | undefined) => void;
  onCalculatedByChange: (val: string | undefined) => void;
  onRangesChange: (ranges: Range[]) => void;
  readOnly?: boolean;
  pell?: boolean;
};

const DefinitionTabContent = ({
  awardType,
  calculatedBy,
  ranges,
  academicRanks = [],
  onAwardTypeChange,
  onCalculatedByChange,
  onRangesChange,
  readOnly,
  pell,
}: DefinitionTabContentProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // A Pell regime's Non-Aid amount duplicates its base sibling 1:1, so the
  // Definition tab's range tags stay consistent with the Award Calculation
  // table, which hides that column for the same reason.
  const displayRanges = useMemo(() => {
    const sorted = sortRangesForDisplay(ranges);

    return sorted.some(isPellRange)
      ? sorted.filter((range) => !isNonAidRange(range))
      : sorted;
  }, [ranges]);

  const rangesLabel = getRangesLabel(awardType);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSaveRanges = useCallback(
    (updatedRanges: Range[]) => {
      onRangesChange(sortRangesForDisplay(updatedRanges));
      setIsModalOpen(false);
    },
    [onRangesChange]
  );

  return (
    <Stack
      sx={{
        position: 'relative',
        gap: 1.5,
        p: 1.5,
        flex: '1 1 0%',
        minHeight: 0,
        overflowY: 'auto',
      }}
      data-testid="definition-tab-content"
      inert={readOnly}
    >
      <Stack sx={{ gap: 0.75 }}>
        <span
          id="award-type-label"
          className="text-base font-semibold text-gray-700"
        >
          Award Need based funds using
        </span>
        <RadioGroup
          aria-labelledby="award-type-label"
          size="small"
          value={awardType}
          onChange={(_event, value) => onAwardTypeChange(value)}
        >
          {AWARD_TYPE_OPTIONS.map((option) => (
            <Radio
              key={option.value}
              value={option.value}
              label={option.label}
              id={`award-type-${option.value}`}
            />
          ))}
        </RadioGroup>
      </Stack>

      <Stack sx={{ gap: 0.75 }}>
        <span className="text-base font-semibold text-gray-700">
          {rangesLabel}
        </span>
        {!displayRanges.length ? (
          !pell && (
            <Button
              type="button"
              variant="reserve-text"
              size="xs"
              iconBefore={<Icons.Plus />}
              onClick={handleOpenModal}
              className="w-fit"
              data-testid="add-ranges-button"
            >
              Add ranges
            </Button>
          )
        ) : (
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
          >
            {displayRanges.map((range, index) => (
              <span
                key={index}
                className="border border-navy-200 rounded-md px-3 py-1 text-base text-gray-700 bg-white whitespace-nowrap"
                data-testid={`range-tag-${index}`}
              >
                {formatRangeLabel(range)}
              </span>
            ))}
            {!pell && (
              <IconButton
                variant="reserve-text"
                size="sm"
                icon={<Icons.EditPencil />}
                onClick={handleOpenModal}
                aria-label="Edit ranges"
                data-testid="edit-ranges-button"
              />
            )}
          </Stack>
        )}
      </Stack>

      <Stack sx={{ gap: 0.75 }}>
        <span
          id="calculated-by-label"
          className="text-base font-semibold text-gray-700"
        >
          Calculated by
        </span>
        <RadioGroup
          aria-labelledby="calculated-by-label"
          row
          size="small"
          value={calculatedBy}
          onChange={(_event, value) => onCalculatedByChange(value)}
        >
          {CALCULATED_BY_OPTIONS.map((option) => (
            <Radio
              key={option.value}
              value={option.value}
              label={option.label}
              id={`calculated-by-${option.value}`}
            />
          ))}
        </RadioGroup>
      </Stack>

      <Stack sx={{ gap: 0.75 }}>
        <span className="text-base font-semibold text-gray-700">
          Academic/Scholarship
        </span>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          {academicRanks.map((ar) => (
            <span
              key={ar.id}
              className="border border-navy-200 rounded-md px-3 py-1 text-base text-gray-700 bg-white"
              data-testid={`academic-rate-${ar.rank}`}
            >
              {ar.rank}
            </span>
          ))}
        </Stack>
      </Stack>

      {isModalOpen && (
        <EditRangesModal
          awardType={awardType}
          ranges={ranges}
          onSave={handleSaveRanges}
          onClose={handleCloseModal}
        />
      )}
    </Stack>
  );
};

export default DefinitionTabContent;
